/**
 * "Remembered on this device" — after a successful booking the browser keeps
 * the client's name/phone/email (+ their reminders answer) so the next visit
 * is prefilled with no lookup, no code, no network. Shared by the full booking
 * widget and the same-day quick-book surface (same key, same record), and
 * cleared by "Not you?". Nothing leaves the device.
 */
export interface RememberedClient {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  smsOpt: 'yes' | 'no' | '';
  marketing?: boolean;
  clientId?: number;
  ts: number;
}

export const REMEMBER_TTL_MS = 1000 * 60 * 60 * 24 * 365;

export function rememberKey(apiBase: string): string {
  return `pp:quick:${(apiBase || '').replace(/[^a-z0-9]/gi, '').slice(-24)}`;
}

export function loadRemembered(apiBase: string, now: number = Date.now(), key?: string): RememberedClient | null {
  try {
    const raw = localStorage.getItem(key || rememberKey(apiBase));
    if (!raw) return null;
    const r = JSON.parse(raw) as RememberedClient;
    if (!r || !r.phone || (now - (r.ts || 0)) > REMEMBER_TTL_MS) return null;
    return r;
  } catch { return null; }
}

export function saveRemembered(apiBase: string, r: Omit<RememberedClient, 'ts'>, now: number = Date.now(), key?: string): void {
  if (!r.phone) return;
  try { localStorage.setItem(key || rememberKey(apiBase), JSON.stringify({ ...r, ts: now })); } catch { /* quota / private mode */ }
}

export function forgetRemembered(apiBase: string, key?: string): void {
  try { localStorage.removeItem(key || rememberKey(apiBase)); } catch { /* ignore */ }
}

// ── Recognition token ───────────────────────────────────────────────────────
//
// Issued when a public booking completes, and kept APART from the portal
// session on purpose: it reaches exactly one read (which memberships this
// client holds) and would fail everything the portal session does. Storing it
// under the session's key would have the silent restore try a profile fetch,
// fail, and wipe it on the next load.

export interface RecognitionToken {
  token: string;
  clientId: number;
  expiresAt: number;
}

function recognitionKey(apiBase: string): string {
  return `pp-recognised:${apiBase || 'default'}`;
}

export function saveRecognition(apiBase: string, token: string, clientId: number, days: number): void {
  if (!token || !clientId) return;
  try {
    localStorage.setItem(recognitionKey(apiBase), JSON.stringify({
      token, clientId, expiresAt: Date.now() + (days || 30) * 86400000,
    }));
  } catch { /* quota / private mode */ }
}

export function loadRecognition(apiBase: string, now: number = Date.now()): RecognitionToken | null {
  try {
    const raw = localStorage.getItem(recognitionKey(apiBase));
    if (!raw) return null;
    const r = JSON.parse(raw) as RecognitionToken;
    if (!r || !r.token || !r.clientId || now > (r.expiresAt || 0)) {
      localStorage.removeItem(recognitionKey(apiBase));
      return null;
    }
    return r;
  } catch { return null; }
}

export function forgetRecognition(apiBase: string): void {
  try { localStorage.removeItem(recognitionKey(apiBase)); } catch { /* ignore */ }
}
